import { z } from "zod";
import { agentArchSchema, agentOsSchema, agentUpdateChannelSchema } from "../agents/enums.js";
import { supportSessionSchema } from "../auth/auth.js";
import { featureKeySchema } from "../auth/features.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { billingProfileSchema, platformInvoiceSummarySchema, subscriptionSchema, tenantUsageSchema, } from "./billing.js";
/**
 * Platform paneli sözleşmeleri — kademeli yayın, ajan sürümleri, destek erişimi, sistem sağlığı, dışa aktarım ve kapanış,
 * duyurular, istasyon devri; kiracı tarafı (aboneliğim, destek erişimi onayı, devir onayı, duyuru merkezi). ADR 0024.
 */
const versionSchema = z.number().int().positive();
const reasonSchema = z.string().trim().min(3, "En az 3 karakter").max(500);
const refSchema = z.object({ id: uuidSchema, name: z.string() });
// ── Kademeli yayın ────────────────────────────────────────────────────────────
export const featureRolloutSchema = z.object({
    featureKey: featureKeySchema,
    label: z.string(),
    strategy: z.enum(["PERCENT", "LIST"]),
    percent: z.number().int(),
    tenants: z.array(refSchema),
    eligibleTenants: z.number().int().describe("Planı bu özelliği içeren kiracı sayısı"),
    includedTenants: z.number().int().describe("Yayın kümesindeki (özellik açık) kiracı sayısı"),
    note: z.string().nullable(),
    updatedAt: timestampSchema,
    version: z.number().int(),
});
export const featureRolloutListSchema = z.object({ items: z.array(featureRolloutSchema) });
export const saveFeatureRolloutSchema = z
    .object({
    strategy: z.enum(["PERCENT", "LIST"]),
    percent: z.number().int().min(0).max(100).default(0),
    tenantIds: z.array(uuidSchema).max(500).default([]),
    note: z.string().trim().max(300).nullable().default(null),
    version: z.number().int().nonnegative().describe("Yeni yayın için 0"),
})
    .superRefine((value, context) => {
    if (value.strategy === "LIST" && value.tenantIds.length === 0)
        context.addIssue({ code: "custom", path: ["tenantIds"], message: "En az bir kiracı seçin" });
});
// ── Ajan sürümleri ────────────────────────────────────────────────────────────
export const agentVersionDistributionSchema = z.object({
    items: z.array(z.object({
        channel: agentUpdateChannelSchema,
        version: z.string(),
        os: agentOsSchema,
        arch: agentArchSchema,
        agents: z.number().int(),
        online: z.number().int(),
    })),
    totals: z.object({ agents: z.number().int(), online: z.number().int(), beta: z.number().int() }),
    tenants: z.array(z.object({
        tenant: refSchema,
        agents: z.number().int(),
        beta: z.number().int(),
        versions: z.array(z.string()),
    })),
});
export const setTenantAgentChannelSchema = z.object({ channel: agentUpdateChannelSchema });
export const setAgentReleaseRolloutSchema = z.object({ rolloutPercent: z.number().int().min(0).max(100) });
// ── Destek erişimi ────────────────────────────────────────────────────────────
export const SUPPORT_GRANT_STATUSES = [
    "REQUESTED",
    "ACTIVE",
    "DENIED",
    "REVOKED",
    "EXPIRED",
    "CANCELLED",
];
export const supportGrantStatusSchema = z.enum(SUPPORT_GRANT_STATUSES);
export const SUPPORT_GRANT_STATUS_LABELS = {
    REQUESTED: "Onay bekliyor",
    ACTIVE: "Etkin",
    DENIED: "Reddedildi",
    REVOKED: "Kiracı iptal etti",
    EXPIRED: "Süresi doldu",
    CANCELLED: "Sonlandırıldı",
};
export const supportGrantSchema = z.object({
    id: uuidSchema,
    tenant: refSchema,
    staff: z.object({ id: uuidSchema, fullName: z.string() }),
    reason: z.string(),
    ticketRef: z.string().nullable(),
    requestedHours: z.number().int(),
    status: supportGrantStatusSchema,
    requestedAt: timestampSchema,
    decidedBy: z.string().nullable(),
    decidedAt: timestampSchema.nullable(),
    decisionNote: z.string().nullable(),
    startsAt: timestampSchema.nullable(),
    expiresAt: timestampSchema.nullable(),
    endedAt: timestampSchema.nullable(),
    requestCount: z.number().int().describe("Kayıtlı istek sayısı"),
    version: z.number().int(),
});
export const supportGrantListQuerySchema = pageQuerySchema.extend({
    tenantId: uuidSchema.optional(),
    status: supportGrantStatusSchema.optional(),
    mine: z.enum(["true"]).optional(),
});
export const supportGrantListSchema = pageOf(supportGrantSchema);
export const requestSupportAccessSchema = z.object({
    tenantId: uuidSchema,
    reason: reasonSchema,
    ticketRef: z.string().trim().max(60).nullable().default(null),
    hours: z.number().int().min(1).max(8),
});
export const decideSupportAccessSchema = z.object({
    note: z.string().trim().max(500).nullable().default(null),
    version: versionSchema,
});
export const supportAccessEventSchema = z.object({
    id: uuidSchema,
    kind: z.enum(["ENTER", "REQUEST", "EXIT"]),
    method: z.string().nullable(),
    path: z.string().nullable(),
    statusCode: z.number().int().nullable(),
    occurredAt: timestampSchema,
});
export const supportAccessEventListSchema = pageOf(supportAccessEventSchema);
export const activeSupportAccessSchema = z.object({
    active: z.array(supportSessionSchema),
    pendingRequests: z.number().int(),
});
// ── Sistem sağlığı ────────────────────────────────────────────────────────────
export const platformHealthSchema = z.object({
    generatedAt: timestampSchema,
    queues: z.array(z.object({
        name: z.string(),
        waiting: z.number().int(),
        active: z.number().int(),
        delayed: z.number().int(),
        failed: z.number().int(),
    })),
    failedJobs: z.array(z.object({
        queue: z.string(),
        id: z.string(),
        name: z.string(),
        failedReason: z.string().nullable(),
        attemptsMade: z.number().int(),
        finishedAt: timestampSchema.nullable(),
    })),
    outbox: z.object({
        pending: z.number().int(),
        oldestPendingAt: timestampSchema.nullable(),
        lagSeconds: z.number().int(),
    }),
    edocumentErrors: z.array(z.object({ tenant: refSchema, invoices: z.number().int(), despatches: z.number().int() })),
    offlineAgents: z.object({
        total: z.number().int(),
        items: z.array(z.object({
            tenant: refSchema,
            stationCode: z.string(),
            hostname: z.string(),
            version: z.string(),
            lastSeenAt: timestampSchema.nullable(),
        })),
    }),
    database: z.object({
        sizeBytes: z.string().describe("bigint, dizge"),
        tables: z.array(z.object({ name: z.string(), totalBytes: z.string(), estimatedRows: z.string() })),
    }),
});
// ── Dışa aktarım ve kapanış ───────────────────────────────────────────────────
export const tenantDataExportSchema = z.object({
    id: uuidSchema,
    tenantId: uuidSchema,
    requestedVia: z.enum(["PLATFORM", "TENANT", "CLOSURE"]),
    requestedBy: z.string().nullable(),
    status: z.enum(["QUEUED", "RUNNING", "READY", "FAILED", "EXPIRED"]),
    sizeBytes: z.string().nullable(),
    tableCount: z.number().int().nullable(),
    rowCount: z.string().nullable(),
    error: z.string().nullable(),
    createdAt: timestampSchema,
    completedAt: timestampSchema.nullable(),
    expiresAt: timestampSchema.nullable(),
});
export const tenantDataExportListSchema = z.object({ items: z.array(tenantDataExportSchema) });
export const exportDownloadSchema = z.object({ url: z.string(), expiresAt: timestampSchema });
export const CLOSURE_REASON_KINDS = ["CONTRACT_END", "KVKK_REQUEST", "OTHER"];
export const CLOSURE_REASON_LABELS = {
    CONTRACT_END: "Sözleşme sonu",
    KVKK_REQUEST: "KVKK talebi",
    OTHER: "Diğer",
};
export const tenantClosureSchema = z.object({
    tenantId: uuidSchema,
    reasonKind: z.enum(CLOSURE_REASON_KINDS),
    reason: z.string(),
    status: z.enum(["CLOSED", "ANONYMIZED"]),
    closedAt: timestampSchema,
    closedBy: z.string().nullable(),
    retentionUntil: localDateSchema,
    anonymizationDue: z.boolean(),
    exportId: uuidSchema.nullable(),
    anonymizedAt: timestampSchema.nullable(),
    anonymizationResult: z.record(z.string(), z.number()).nullable(),
    version: z.number().int(),
});
export const closeTenantSchema = z.object({
    reasonKind: z.enum(CLOSURE_REASON_KINDS),
    reason: reasonSchema,
    confirmName: z.string().trim().min(1).describe("Kiracı adı birebir yazılır"),
});
export const anonymizeTenantSchema = z.object({
    confirmName: z.string().trim().min(1),
    version: versionSchema,
});
// ── Duyurular ─────────────────────────────────────────────────────────────────
export const ANNOUNCEMENT_KINDS = ["RELEASE_NOTES", "ANNOUNCEMENT", "MAINTENANCE"];
export const announcementKindSchema = z.enum(ANNOUNCEMENT_KINDS);
export const ANNOUNCEMENT_KIND_LABELS = {
    RELEASE_NOTES: "Sürüm notu",
    ANNOUNCEMENT: "Duyuru",
    MAINTENANCE: "Planlı bakım",
};
export const announcementSeveritySchema = z.enum(["INFO", "IMPORTANT"]);
export const announcementAudienceSchema = z.enum(["ALL", "PLANS", "TENANTS"]);
export const platformAnnouncementSchema = z.object({
    id: uuidSchema,
    kind: announcementKindSchema,
    severity: announcementSeveritySchema,
    title: z.string(),
    body: z.string(),
    audience: announcementAudienceSchema,
    planCodes: z.array(z.string()),
    tenants: z.array(refSchema),
    status: z.enum(["DRAFT", "PUBLISHED", "ARCHIVED"]),
    publishedAt: timestampSchema.nullable(),
    expiresAt: timestampSchema.nullable(),
    readCount: z.number().int(),
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const platformAnnouncementListSchema = z.object({ items: z.array(platformAnnouncementSchema) });
const announcementBody = {
    kind: announcementKindSchema,
    severity: announcementSeveritySchema.default("INFO"),
    title: z.string().trim().min(3).max(160),
    body: z.string().trim().min(3).max(10_000),
    audience: announcementAudienceSchema.default("ALL"),
    planCodes: z.array(z.string().max(40)).max(20).default([]),
    tenantIds: z.array(uuidSchema).max(500).default([]),
    expiresAt: timestampSchema.nullable().default(null),
};
const audienceRule = (value, context) => {
    if (value.audience === "PLANS" && (value.planCodes ?? []).length === 0)
        context.addIssue({ code: "custom", path: ["planCodes"], message: "En az bir plan seçin" });
    if (value.audience === "TENANTS" && (value.tenantIds ?? []).length === 0)
        context.addIssue({ code: "custom", path: ["tenantIds"], message: "En az bir kiracı seçin" });
};
export const createAnnouncementSchema = z.object(announcementBody).superRefine(audienceRule);
export const replaceAnnouncementSchema = z
    .object({ ...announcementBody, version: versionSchema })
    .superRefine(audienceRule);
export const publishAnnouncementSchema = z.object({
    publishAt: timestampSchema.nullable().default(null).describe("Boş → hemen"),
    version: versionSchema,
});
export const archiveAnnouncementSchema = z.object({ version: versionSchema });
/** Kiracı tarafı duyuru merkezi. */
export const tenantAnnouncementSchema = z.object({
    id: uuidSchema,
    kind: announcementKindSchema,
    severity: announcementSeveritySchema,
    title: z.string(),
    body: z.string(),
    publishedAt: timestampSchema,
    read: z.boolean(),
});
export const tenantAnnouncementListSchema = z.object({
    items: z.array(tenantAnnouncementSchema),
    unread: z.number().int(),
    unreadImportant: z.number().int(),
});
// ── İstasyon devri ────────────────────────────────────────────────────────────
export const transferReportSchema = z.object({
    moved: z.array(z.string()),
    stayed: z.array(z.string()),
    warnings: z.array(z.string()),
});
export const stationTransferSchema = z.object({
    id: uuidSchema,
    sourceTenant: refSchema,
    sourceStation: z.object({ id: uuidSchema, code: z.string(), name: z.string() }),
    targetTenant: refSchema,
    targetCompany: refSchema,
    targetStationId: uuidSchema.nullable(),
    status: z.enum(["PENDING", "COMPLETED", "CANCELLED"]),
    effectiveDate: localDateSchema,
    note: z.string().nullable(),
    sourceApproval: z.object({ by: z.string(), at: timestampSchema }).nullable(),
    targetApproval: z.object({ by: z.string(), at: timestampSchema }).nullable(),
    requestedBy: z.string().nullable(),
    report: transferReportSchema.nullable(),
    cancelReason: z.string().nullable(),
    createdAt: timestampSchema,
    completedAt: timestampSchema.nullable(),
    cancelledAt: timestampSchema.nullable(),
    /** Kiracı tarafında: bu kiracı hangi taraf, onayı bekleniyor mu. */
    side: z.enum(["SOURCE", "TARGET"]).nullable(),
    canApprove: z.boolean(),
});
export const stationTransferListSchema = z.object({ items: z.array(stationTransferSchema) });
export const createStationTransferSchema = z.object({
    sourceTenantId: uuidSchema,
    sourceStationId: uuidSchema,
    targetTenantId: uuidSchema,
    targetCompanyId: uuidSchema,
    effectiveDate: localDateSchema,
    note: z.string().trim().max(500).nullable().default(null),
});
export const cancelStationTransferSchema = z.object({ reason: reasonSchema });
// ── Kiracı tarafı: aboneliğim ─────────────────────────────────────────────────
export const mySubscriptionSchema = z.object({
    subscription: subscriptionSchema.nullable(),
    planFeatures: z.array(featureKeySchema),
    usage: tenantUsageSchema.nullable(),
    invoices: z.array(platformInvoiceSummarySchema),
    outstanding: z.string().describe("Kuruş"),
    billingProfile: billingProfileSchema.nullable(),
    canManageBilling: z.boolean(),
    canExport: z.boolean(),
});
// ── Yol parametreleri ─────────────────────────────────────────────────────────
export const platformIdParamsSchema = z.object({ id: uuidSchema });
export const platformUserParamsSchema = z.object({ userId: uuidSchema });
export const platformFeatureParamsSchema = z.object({ featureKey: featureKeySchema });
export const platformTenantFeatureParamsSchema = z.object({ id: uuidSchema, featureKey: featureKeySchema });
export const platformTransferListQuerySchema = z.object({ tenantId: uuidSchema.optional() });
export const platformPlanParamsSchema = z.object({ code: z.string().regex(/^[A-Z][A-Z0-9_]{1,39}$/) });
export const platformDiscountParamsSchema = z.object({ id: uuidSchema, discountId: uuidSchema });
export const runPlatformBillingSchema = z.object({
    tenantId: uuidSchema.nullable().default(null).describe("Boş → tüm kiracılar"),
});
export const platformExportParamsSchema = z.object({ id: uuidSchema, exportId: uuidSchema });
export const supportAccessEventListQuerySchema = pageQuerySchema;
//# sourceMappingURL=operations.js.map